import React from 'react';
import {
    BookOpen,
    Info,
    CheckCircle2,
    Lightbulb,
    Square
} from 'lucide-react';

const FoodAccommodationsIntro = ({ onStart }) => {
    const accommodations = [
        'Vegan', 'Vegetarian',
        'Gluten-Free', 'Dairy-Free',
        'Nut-Free', 'Soy-Free',
        'Sesame-Free', 'None'
    ];

    const definitions = [
        { label: 'Vegan:', text: 'Contains no animal products (no meat, dairy, eggs, or honey).' },
        { label: 'Vegetarian:', text: 'Contains no meat, poultry, or fish; may include dairy or eggs.' },
        { label: 'Gluten-Free:', text: 'Contains no gluten ingredients; prepared without gluten-containing items.' },
        { label: 'Dairy-Free:', text: 'Contains no milk or milk-derived ingredients.' },
        { label: 'Nut-Free:', text: 'Contains no tree nuts or peanuts as ingredients.' },
        { label: 'Soy-Free:', text: 'Contains no soy or soy-derived ingredients.' },
        { label: 'Sesame-Free:', text: 'Contains no sesame (seeds, oil, or paste).' },
        { label: 'None:', text: 'The dish does not naturally meet any of these accommodations.' },
    ];

    return (
        <div className="min-h-screen bg-[#fcf7ec] py-12 px-4 sm:px-6 lg:px-8 font-sans text-gray-900 flex flex-col items-center">
            <div className="max-w-4xl w-full bg-white rounded-3xl shadow-sm p-8 sm:p-10 relative">

                {/* Header Pill */}
                <div className="flex justify-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-1.5 rounded-full text-sm font-medium text-gray-600">
                        <BookOpen size={17} className="text-gray-500" />
                        Chapter intro
                    </div>
                </div>

                {/* Title & Description */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        What "Accommodations" means here
                    </h1>
                    <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                        Before you begin, review how we define food accommodations so you can tag
                        dishes accurately and guide guests with confidence.
                    </p>
                </div>

                {/* Green Section - Definition */}
                <div className="bg-[#F0FDF4] rounded-2xl p-6 mb-6 border border-green-100">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="bg-[#15803D] p-1.5 rounded-md">
                            <Info className="text-white w-4 h-4" />
                        </div>
                        <span className="bg-[#15803D] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                            Definition
                        </span>
                    </div>

                    <h3 className="font-bold text-gray-900 text-lg mb-1 text-left">
                        Accommodations are the dietary needs a dish naturally meets.
                    </h3>
                    <p className="text-gray-600 text-left">
                        They describe inherent qualities of the dish in its original form—no substitutions
                        or changes required.
                    </p>
                </div>

                {/* Blue Section - Select all that apply */}
                <div className="bg-[#EFF6FF] rounded-2xl p-6 mb-6 border border-blue-100">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-[#2563EB] p-1.5 rounded-md">
                            <CheckCircle2 className="text-white w-4 h-4" />
                        </div>
                        <span className="bg-[#2563EB] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                            Select all that apply
                        </span>
                    </div>

                    <h3 className="font-bold text-gray-900 mb-4 text-left">
                        Choose every accommodation the dish already meets:
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {accommodations.map((item) => (
                            <div key={item} className="bg-white p-3 rounded-lg border border-blue-50 shadow-sm text-left font-medium text-gray-700">
                                {item}
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                        <BookOpen className="text-[#2563EB] w-4 h-4" />
                        <span className="font-bold text-gray-900 text-base">Quick definitions</span>
                    </div>

                    <div className="space-y-2 bg-white p-3 rounded-lg border border-blue-50 shadow-sm">
                        {definitions.map((def, idx) => (
                            <div key={idx} className="text-sm text-left">
                                <span className="font-bold text-base text-gray-900">{def.label}</span>
                                <span className="text-gray-600 ml-1">{def.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Purple Section - How this differs */}
                <div className="bg-[#FAF5FF] rounded-2xl p-6 mb-6 border border-purple-100">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="bg-[#7E22CE] p-1.5 rounded-md">
                            <Square className="text-white w-4 h-4 fill-current" />
                        </div>
                        <span className="bg-[#7E22CE] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                            How this differs from Allergens & Restrictions
                        </span>
                    </div>

                    <h3 className="font-bold text-gray-900 mb-3 text-left">
                        Accommodations describe what a dish already fits; Allergens/Health list
                        what's included.
                    </h3>
                    <div className="space-y-1 text-sm text-left">
                        <p className="text-gray-700">
                            <span className="font-bold">• Allergens/Health</span> = ingredients that are present and may affect safety.
                        </p>
                        <p className="text-gray-700">
                            <span className="font-bold">• Accommodations</span> = who the dish suits as served (no modifications).
                            <br />
                            <span className="ml-3">Use both for clear guest guidance.</span>
                        </p>
                    </div>
                </div>

                {/* Yellow Section - Important Notes */}
                <div className="bg-[#FFFBEB] rounded-2xl p-6 mb-12 border border-yellow-100">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-yellow-600"><Lightbulb size={20} /></span>
                        <h3 className="font-bold text-gray-900">Important Notes</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-700 text-left list-disc list-inside marker:text-gray-400">
                        <li>Mark accommodations only when the dish meets them without modification.</li>
                        <li>If a change is required to meet a need, that is a substitution—not an accommodation.</li>
                        <li>When unsure, check the recipe or ask a manager/chef.</li>
                    </ul>
                </div>

                {/* Start Button */}
                <div className="flex justify-end">
                    <button
                        onClick={onStart}
                        className="bg-[#A01B3F] hover:bg-[#8a1736] text-white font-bold py-2 px-8 rounded-xl transition-all shadow-md"
                    >
                        Start
                    </button>
                </div>

            </div>
        </div>
    );
};

export default FoodAccommodationsIntro;
